import { Button, Stack, Typography } from '@mui/material'
import { useImpersonatingAuthToken } from 'hooks/use-auth-token'
import { impersonatedUserAtom, organizationFamily } from 'state'
import { useRecoilState, useRecoilValue } from 'recoil'
import { useActiveOrganization, useImpersonatingOrganization } from 'hooks/use-organization'
import { OrganizationFull, User } from 'api/model'
import OrganizationLogo from 'components/OrganizationLogo'
import { COLORS, UI_SIZES } from 'app-constants'

const OrganizationInfoBar = ({ user }: { user: User }) => {
  const impersonatedUser = useRecoilValue(impersonatedUserAtom)

  const [activeOrganization] = useActiveOrganization()
  const organization = useRecoilValue(organizationFamily(activeOrganization ?? ''))

  if (!organization) {
    return null
  }

  const bannerStyle: () => React.CSSProperties = () => {
    if (impersonatedUser) {
      return {
        background: COLORS.JELLED_PRIMARY_BLUE,
        color: COLORS.PURE_WHITE,
      }
    }

    return {
      background: COLORS.GRAY_8,
      color: COLORS.GRAY_0,
    }
  }

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      style={{
        position: 'fixed',
        right: '24px',
        borderRadius: '8px',
        top: '24px',
        width: 'fit-content',
        height: `${UI_SIZES.ORG_INFO_BAR_HEIGHT}px`,
        zIndex: 1000,
        ...bannerStyle(),
      }}>
      {impersonatedUser ? (
        <ImpersonateContent organization={organization} />
      ) : (
        <OrganizationContent organization={organization} />
      )}
    </Stack>
  )
}

const ImpersonateContent = ({ organization }: { organization: OrganizationFull }) => {
  const [impersonatedUser, setImpersonatedUser] = useRecoilState(impersonatedUserAtom)

  const [, setAuthToken] = useImpersonatingAuthToken()
  const [, setImpersonatingOrganization] = useImpersonatingOrganization()

  const onDisconnectClicked = () => {
    setAuthToken(null)
    setImpersonatedUser(null)
    setImpersonatingOrganization(null)

    window.location.pathname = ''
  }

  return (
    <>
      <Typography variant="h5" pl={2}>
        {organization?.name}
      </Typography>

      <Stack direction="row" alignItems="center" spacing={2} pr={2}>
        <Typography variant="pXS" ml={2}>
          Viewing as {impersonatedUser?.name || impersonatedUser?.email}
        </Typography>
        <Button variant="outlined" size="small" color="white" onClick={onDisconnectClicked}>
          Disconnect
        </Button>
      </Stack>
    </>
  )
}

const OrganizationContent = ({ organization }: { organization: OrganizationFull }) => {
  return (
    <Stack direction="row" alignItems="center" spacing={1} pl={2}>
      <OrganizationLogo
        organization={organization}
        style={{ border: 'none', height: '24px', width: '24px', borderRadius: '4.8px', fontSize: '18px' }}
      />
      <Typography variant="h5">{organization.name}</Typography>
    </Stack>
  )
}

export default OrganizationInfoBar
